import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import Articles from '../../articlesData';
import ArticleWrapper from '../../components/ArticleWrapper';
import { AudioPlayer } from '../../components/AudioProvider';
import { ExploreMoreLinks, ArticleTitleSection } from '../../components/ArticleComponents';


const ModeEnvoy = () => {
  const navigate = useNavigate();
  const article = Articles.find((a) => a.id === 'mode_envoy');
  useEffect(() => {
    if (!article.visible) {
      navigate('/in-progress');
    }
  }, [navigate, article.visible]);

  return (
    <ArticleWrapper articleId="mode_envoy">
      <div className="min-h-screen bg-[#111112] flex flex-col w-full">
        <ArticleTitleSection
          image='/pictures/articles/envoy/banner.JPG'
          title='Mode Envoy Review'
          date={article.date}
          status={article.status}
        />
        {/* Content section with side-by-side layout */}
          <div className="w-full flex flex-col gap-16 px-4 bp1080:px-8 mb-16">

            <div className="w-full flex flex-col bp1080:flex-row items-center gap-8">
              <img
                src="/pictures/articles/envoy/pic1.JPG"
                alt="Pavlov Design"
                className="w-full bp1080:w-[47vw] h-[87vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center bp1080:text-left bp1080:ml-[3vw] bp1080:max-w-[54vw]">
                <h2 className="text-3xl font-bold mb-4">Overview</h2>
                <p>
                  The <b>Mode Envoy</b> is a 65% mechanical keyboard that aims to deliver a premium experience at a more accessible price point. Released by Mode Designs, the Envoy quickly earned a reputation as one of the best “budget” customs on the market. Starting at around {' '}
                  <a
                    href="https://modedesigns.com/products/envoy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 underline hover:text-blue-300"
                  >
                    $200
                  </a>, it offers a sleek, minimalist design, versatile mounting system, and impressive build quality that rivals boards well above its price range. 
                </p><br />
                <h2 className="text-3xl font-bold mb-4">What It Does Well</h2>
                <p>
                  Its standout feature is the <b>lattice block mounting system</b>, which delivers a unique combination of firmness and bounce, producing a signature <b>clacky, high-pitched sound</b> that appeals to enthusiasts seeking sharp acoustic feedback. Available in both <b>aluminum and polycarbonate cases</b>, with optional upgrades like brass or copper weights, the Envoy provides a high degree of customization while maintaining Mode’s trademark fit and finish.                
                </p><br />
                <h2 className="text-3xl font-bold mb-4">One Issue</h2>
                <p>
                  However, it does come with one notable flaw out of the box. But with a simple fix, the Envoy remains a compelling option for anyone seeking a distinctive, in-stock, and high-performance 65% keyboard in today’s crowded market.
                </p>
              </div>
            </div>

            <div className="w-full flex flex-col md:flex-row-reverse items-center gap-8">
              <img
                src="/pictures/articles/envoy/pic2.JPG"
                alt="Pavlov Case"
                className="w-full md:w-[45vw] h-[90vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center md:mr-[2vw] md:text-left md:max-w-[45vw]">
                <h2 className="text-3xl font-bold mb-4">The Fix</h2>
                <p>
                  When installing the large screw standoffs used to keep the PCB assembly from falling out of the case, be sure to <b>remove the rubber dampeners</b> and screw them in <b>very lightly</b>, stopping as soon as you feel the slightest resistance. If installed normally, they can significantly affect the typing feel and sound quality, dulling the board’s natural clack and bounce.
                  <br /><br />
                   Check out the {' '}
                  <a
                    href="https://youtu.be/yPUSsM9YeTc?si=J6hRP_nnZiR39ahG&t=456"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 underline hover:text-blue-300"
                  >
                    review video
                  </a>
                  {' '} for visual guidance and before & after comparisons!
                </p><br />
                <h2 className="text-3xl font-bold mb-4">Competition</h2>
                <p className="mb-4">In recent years, the <b>Mode Envoy</b> has faced increasing competition from a new wave of high-quality, budget-friendly keyboards, most notably the <b>Neo65 Core</b> and <b>Neo65 Cu</b>.</p>
                <br />
                <h2 className="text-3xl font-bold mb-4">Neo65 Core</h2>
                <p className="mb-4">The <b>Neo65 Core</b> undercuts the Envoy by <b>$60-80</b> while offering excellent build quality, flexible plate options, and a well-balanced sound profile right out of the box. It also includes extras like foam kits that are sold separately for the Envoy, making it a more complete package for first-time builders or those looking for value.</p>

              </div>
            </div>

            <div className="w-full flex flex-col md:flex-row items-center gap-8">
              <img
                src="/pictures/articles/envoy/pic3.JPG"
                alt="Pavlov Typing"
                className="w-full md:w-[60vw] h-[60vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center md:text-left">
              <h2 className="text-3xl font-bold mb-4">Neo65 Cu</h2>
              <p className="mb-4">On the higher end, the <b>Neo65 Cu</b> enters the ring with standout materials, featuring a <b>full brass or copper bottom</b> at a similar or slightly higher price point (after shipping). 
              <br /><br />
              While it trades the Envoy’s high-pitched clack for a <b>deeper</b>, <b>weightier sound</b>, the Cu’s visual presence and premium heft appeal to users wanting a more substantial desk piece. With options like these, the Envoy is no longer the clear default. It now has to compete not just on sound and feel, but also on price, aesthetics, and out-of-the-box value.
              </p>
              </div>
            </div>

            <div className="w-full flex flex-col md:flex-row-reverse items-center gap-8">
              <img
                src="/pictures/articles/envoy/pic4.JPG"
                alt="Pavlov Sound Test"
                className="w-full md:w-[45vw] h-[110vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center md:text-left md:mr-[2vw] md:max-w-[45vw]">
                <h2 className="text-3xl font-bold mb-4">What to pick?</h2>
                <p className="mb-4">Choosing between the <b>Mode Envoy</b>, <b>Neo65 Core</b>, and <b>Neo65 Cu</b> ultimately comes down to your priorities. 
                <br /><br />
                If you're after a <b>high-pitched, clacky sound</b>, appreciate the <b>clean and refined aesthetic</b>, or want a <b>polycarbonate case with RGB</b>, the <b>Mode Envoy</b> is still a great option.
                <br /><br />
                On the other hand, if you're looking for the <b>best value</b> and prefer more <b>flexibility</b> out of the box, the <b>Neo65 Core</b> is hard to beat, offering excellent sound variation and build quality at a lower price. 
                <br /><br />
                If you want something more <b>premium and unique</b>, the Neo65 Cu stands out with its <b>full brass or copper bottom</b>, deeper sound profile, and heavyweight presence. It’s ideal for users who care about materials and visual impact as much as acoustics.
                <br /><br />
                Here's some soundtests for comparison:
                </p>
                <AudioPlayer src="/audio/envoy/envoy_nixies.mp3" className="w-full mt-5" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>White Envoy: Alu Plate, Green Lattice, Nixies, No Foams</p>
                <AudioPlayer src="/audio/envoy/envoypc_milkyyellow_green.mp3" className="w-full mt-2" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>PC Envoy: Alu Plate, Green Lattice, Gateron Milky Yellow, No Foams</p>
                <AudioPlayer src="/audio/neo65cu/neo65cu_mx2a.mp3" className="w-full mt-2" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>Neo65 Cu: CF plate, Gasket Mount, Cherry MX2A Black, No Foams</p>
                <AudioPlayer src="/audio/neo65cu/neo65cu_kguni.mp3" className="w-full mt-2" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>Neo65 Cu: PP plate, Isolated Top Mount, Keygeek Uni, No Foams</p>
              </div>
            </div>

            <div className="w-full flex flex-col md:flex-row items-center gap-8">
              <img
                src="/pictures/articles/envoy/pic6.JPG"
                alt="Pavlov Typing"
                className="w-full md:w-[53vw] h-[98.5vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center md:text-left ml-[0.7vw]">
                <h2 className="text-3xl font-bold mb-4">Mounting Styles and Case Materials</h2>
                <p className="mb-4">The biggest difference between the black and green mounting blocks is the <b>typing feel</b>. The black mounts are significantly <b>stiffer</b> than the green lattice mounts. This also results in a slightly <b>deeper</b> sound, especially on the modifiers. 
                <br /><br />
                Regarding case materials, polycarbonate produces a slightly more <b>muted</b> and <b>deeper</b> sound compared to aluminum, while still retaining Mode’s signature clack. It also reduces the <b>metallic resonance</b> often found in aluminum cases. Additionally, the PC case supports <b>RGB lighting</b>, though the LED diffusion is somewhat uneven.
                <br /><br />
                Here's some soundtests for comparison:
                </p>
                <AudioPlayer src="/audio/envoy/envoy_nixies.mp3" className="w-full mt-5" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>White Envoy: Alu Plate, Green Lattice, Nixies, No Foams</p>
                <AudioPlayer src="/audio/envoy/envoypc_nixies_green.mp3" className="w-full mt-2" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>PC Envoy: Alu Plate, Green Lattice, Nixies, No Foams</p>
                <AudioPlayer src="/audio/envoy/envoypc_milkyyellow_green.mp3" className="w-full mt-2" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>PC Envoy: Alu Plate, Green Lattice, Gateron Milky Yellow, No Foams</p>
                <AudioPlayer src="/audio/envoy/envoypc_milkyyellow_black.mp3" className="w-full mt-2" />
                <p className='text-[1rem] text-center mt-[0.5vh]'>PC Envoy: Alu Plate, Black Lattice, Gateron Milky Yellow, No Foams</p>
              </div>
            </div>

            <div className="w-full flex flex-col md:flex-row-reverse items-center gap-8">
              <img
                src="/pictures/articles/envoy/pic5.JPG"
                alt="Pavlov Case"
                className="w-full md:w-[55vw] h-[60vh] object-cover rounded-lg shadow-lg"
              />
              <div className="text-gray-200 text-[1.15rem] text-center md:mr-[2vw] md:ml-[1vw] md:text-left md:max-w-[45vw]">
                <h2 className="text-3xl font-bold mb-4">Conclusion</h2>
                <p>
                  In the end, the Mode Envoy still holds its place as a standout 65% keyboard, especially for those seeking a crisp, high-pitched sound and a refined, minimalist aesthetic. Its unique lattice block mount offers a distinctive typing feel that sets it apart from more traditional gasket-mounted competitors. However, with strong alternatives like the Neo65 Core and Cu offering impressive value, customization, and premium materials at competitive prices, the Envoy is no longer the obvious choice for everyone. It’s best suited for users who know what they want: a sharp clack, thoughtful design, and the choice for a polycarbonate case. If that sounds like you, the Envoy delivers. If not, the Neo65 lineup might better match your budget and needs.
                </p>
              </div>
            </div>


          </div>
          {/* Video section */}
        <div className="w-full flex flex-col items-center justify-center mb-24">
          <h2 className="text-3xl font-bold text-white mb-4 text-center">Review & Sound Test</h2>
          <div className="w-full flex justify-center">
            <iframe
              width="900"
              height="506"
              src="https://www.youtube.com/embed/yPUSsM9YeTc?si=uPtCSSQZRowl94h_"
              title="Pavlov Build and Sound Test"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="rounded-lg shadow-lg mx-auto w-full max-w-4xl"
            ></iframe>
          </div>
        </div>
        {/* Links section */}
        <div className="w-full flex flex-col items-center mb-24">
          <ExploreMoreLinks/>
        </div>
      </div>
    </ArticleWrapper>
  );
};

export default ModeEnvoy;


